import React, { useState, useEffect } from 'react';
import { 
  FinancialData, 
  CapeScore, 
  FScore, 
  PnFChart as PnFChartType,
  TradingSignal
} from '../../types';
import PnFChart from '../charts/PnFChart';
import { tradeStrategyService } from '../../services/tradeStrategy';
import alphaVantageService from '../../services/alphaVantageService';

interface StockDetailProps {
  ticker: string;
  onBack?: () => void;
}

const StockDetail: React.FC<StockDetailProps> = ({ ticker, onBack }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [financialData, setFinancialData] = useState<FinancialData | null>(null);
  const [capeScore, setCapeScore] = useState<CapeScore | null>(null);
  const [fScore, setFScore] = useState<FScore | null>(null);
  const [pnfChart, setPnfChart] = useState<PnFChartType | null>(null);
  const [tradingSignal, setTradingSignal] = useState<TradingSignal | null>(null);
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(undefined);

      try {
        // Fetch all data in parallel for efficiency
        const data = await alphaVantageService.getFinancialData(ticker);
        const cape = await tradeStrategyService.calculateCAPE(ticker, data);
        const fscore = await tradeStrategyService.calculateFScore(ticker, data);
        const chart = tradeStrategyService.generatePnFChart(ticker, data.priceHistory);
        const signal = await tradeStrategyService.generateTradingSignal(ticker, data);
        const price = await alphaVantageService.getCurrentPrice(ticker);

        setFinancialData(data);
        setCapeScore(cape);
        setFScore(fscore);
        setPnfChart(chart);
        setTradingSignal(signal);
        setCurrentPrice(price);
      } catch (err) {
        console.error('Error fetching stock data:', err);
        setError('Failed to fetch stock data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [ticker]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const getFScoreDescription = (score: number): string => {
    if (score >= 7) return 'Strong financial health';
    if (score >= 5) return 'Average financial health';
    if (score >= 3) return 'Below average financial health';
    return 'Poor financial health';
  };
  
  const getCapeDescription = (percentile: number): string => {
    if (percentile <= 20) return 'Potentially undervalued';
    if (percentile <= 40) return 'Slightly undervalued';
    if (percentile <= 60) return 'Fair value';
    if (percentile <= 80) return 'Slightly overvalued';
    return 'Potentially overvalued';
  };

  const getSignalColor = (signal: string): string => {
    switch (signal.toLowerCase()) {
      case 'buy': return 'bg-green-100 text-green-800 border-green-400';
      case 'sell': return 'bg-red-100 text-red-800 border-red-400';
      default: return 'bg-gray-100 text-gray-800 border-gray-400';
    }
  };

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{ticker}</h2>
            {currentPrice && (
              <p className="text-lg text-gray-700">
                Current Price: ${currentPrice.toFixed(2)}
              </p>
            )}
          </div>
          {onBack && (
            <button
              className="py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={onBack}
            >
              ← Back
            </button>
          )}
        </div>
      </div>

      {/* Trading Signal Summary */}
      {tradingSignal && (
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Triple Point Analysis</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">CAPE Analysis</h4>
              <div className="text-2xl font-bold">{tradingSignal.cape.toFixed(2)}</div>
              <div className="text-sm text-gray-600 mb-1">Percentile: {tradingSignal.capePercentile.toFixed(1)}%</div>
              <div className="text-sm text-gray-600">
                {getCapeDescription(tradingSignal.capePercentile)}
              </div>
            </div>
            <div className="border rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">F-Score</h4>
              <div className="text-2xl font-bold">{tradingSignal.f_score} / 9</div>
              <div className="text-sm text-gray-600">
                {getFScoreDescription(tradingSignal.f_score)}
              </div>
            </div>
            <div className="border rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Trading Signal</h4>
              <div className={`inline-block px-3 py-1 text-sm font-bold rounded border ${getSignalColor(tradingSignal.finalSignal)}`}>
                {tradingSignal.finalSignal}
              </div>
              <div className="text-sm text-gray-600 mt-2">
                Preliminary: <span className={`inline-block px-2 py-1 text-xs rounded border ${getSignalColor(tradingSignal.preliminarySignal)}`}>
                  {tradingSignal.preliminarySignal}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* P&F Chart */}
      {pnfChart && (
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Point & Figure Chart</h3>
          <PnFChart chart={pnfChart} />
        </div>
      )}

      {/* F-Score Breakdown */}
      {fScore && (
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Piotroski F-Score Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Profitability Criteria</h4>
              <div className={`flex items-center ${fScore.roa ? 'text-green-600' : 'text-red-600'}`}>
                <span className="mr-2">{fScore.roa ? '✓' : '✗'}</span>
                <span>Positive Return on Assets</span>
              </div>
              <div className={`flex items-center ${fScore.cfo ? 'text-green-600' : 'text-red-600'}`}>
                <span className="mr-2">{fScore.cfo ? '✓' : '✗'}</span>
                <span>Positive Operating Cash Flow</span>
              </div>
              <div className={`flex items-center ${fScore.deltaRoa ? 'text-green-600' : 'text-red-600'}`}>
                <span className="mr-2">{fScore.deltaRoa ? '✓' : '✗'}</span>
                <span>ROA Improvement</span>
              </div>
              <div className={`flex items-center ${fScore.accrual ? 'text-green-600' : 'text-red-600'}`}>
                <span className="mr-2">{fScore.accrual ? '✓' : '✗'}</span>
                <span>CFO &gt; ROA (Quality of Earnings)</span>
              </div>
            </div>
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Leverage & Liquidity</h4>
              <div className={`flex items-center ${fScore.deltaLeverage ? 'text-green-600' : 'text-red-600'}`}>
                <span className="mr-2">{fScore.deltaLeverage ? '✓' : '✗'}</span>
                <span>Decreased Leverage</span>
              </div>
              <div className={`flex items-center ${fScore.deltaLiquidity ? 'text-green-600' : 'text-red-600'}`}>
                <span className="mr-2">{fScore.deltaLiquidity ? '✓' : '✗'}</span>
                <span>Increased Current Ratio</span>
              </div>
              <div className={`flex items-center ${fScore.noEquityRaise ? 'text-green-600' : 'text-red-600'}`}>
                <span className="mr-2">{fScore.noEquityRaise ? '✓' : '✗'}</span>
                <span>No New Shares Issued</span>
              </div>
            </div>
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Operating Efficiency</h4>
              <div className={`flex items-center ${fScore.deltaMargin ? 'text-green-600' : 'text-red-600'}`}>
                <span className="mr-2">{fScore.deltaMargin ? '✓' : '✗'}</span>
                <span>Improved Gross Margin</span>
              </div>
              <div className={`flex items-center ${fScore.deltaTurnover ? 'text-green-600' : 'text-red-600'}`}>
                <span className="mr-2">{fScore.deltaTurnover ? '✓' : '✗'}</span>
                <span>Improved Asset Turnover</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Price History */}
      {financialData && financialData.priceHistory.length > 0 && (
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Price History</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Open
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    High
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Low
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Close
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Volume
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {financialData.priceHistory.slice(0, 10).map((price, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                      {new Date(price.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                      ${price.open.toFixed(2)}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                      ${price.high.toFixed(2)}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                      ${price.low.toFixed(2)}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                      ${price.close.toFixed(2)}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                      {price.volume.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockDetail; 